import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye } from 'lucide-react';

export const FLOWCHART_NODE_HOVER_CARD_ENABLED = true;

const NODE_TYPE_LABELS = {
  process: 'Process (Rectangle)',
  decision: 'Decision (Diamond)',
  start: 'Start',
  end: 'End',
};

function formatNodeTypeLabel(nodeType) {
  const key = String(nodeType || 'process').toLowerCase();
  return NODE_TYPE_LABELS[key] || key || 'Process (Rectangle)';
}

function DetailField({ label, labelClassName = 'text-slate-500', children }) {
  return (
    <div>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelClassName}`}>{label}</p>
      {children}
    </div>
  );
}

/** Round chip — matches flowchart system cylinder colors (key = purple, else light blue). */
function SystemChip({ name, isKey }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
      style={
        isKey
          ? { backgroundColor: '#7c3aed', color: '#ffffff', boxShadow: 'inset 0 0 0 1px #5b21b6' }
          : { backgroundColor: '#dbeafe', color: '#1e3a8a', boxShadow: 'inset 0 0 0 1px #93c5fd' }
      }
      title={isKey ? `${name} (key system)` : name}
    >
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: isKey ? '#fde047' : '#60a5fa' }}
        aria-hidden="true"
      />
      {name}
      {isKey ? <span className="text-[9px] font-bold tracking-wide text-yellow-300">KEY</span> : null}
    </span>
  );
}

/** Chip — matches document folder colors (report = yellow, document = green). */
function DocumentChip({ name, isReport }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium"
      style={
        isReport
          ? { backgroundColor: '#fef3c7', color: '#92400e', boxShadow: 'inset 0 0 0 1px #f59e0b' }
          : { backgroundColor: '#dcfce7', color: '#166534', boxShadow: 'inset 0 0 0 1px #86efac' }
      }
      title={isReport ? `${name} (report)` : name}
    >
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
        style={{ backgroundColor: isReport ? '#fde047' : '#86efac' }}
        aria-hidden="true"
      />
      {name}
      {isReport ? <span className="text-[9px] font-semibold uppercase text-amber-700">report</span> : null}
    </span>
  );
}

function NodeTypeBadge({ nodeType }) {
  const key = String(nodeType || 'process').toLowerCase();
  const styles = {
    process: { bg: '#dbeafe', text: '#1d4ed8', ring: '#93c5fd' },
    decision: { bg: '#ede9fe', text: '#6d28d9', ring: '#c4b5fd' },
    start: { bg: '#d1fae5', text: '#047857', ring: '#6ee7b7' },
    end: { bg: '#fee2e2', text: '#b91c1c', ring: '#fca5a5' },
  };
  const s = styles[key] || styles.process;
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.text, boxShadow: `inset 0 0 0 1px ${s.ring}` }}
    >
      {formatNodeTypeLabel(nodeType)}
    </span>
  );
}

export function FlowchartNodeHoverCard({
  nodeType,
  description,
  actor,
  systems = [],
  documents = [],
}) {
  const [open, setOpen] = useState(false);

  if (!FLOWCHART_NODE_HOVER_CARD_ENABLED) {
    return null;
  }

  const systemItems = (systems || [])
    .map((s) => {
      const name = (s?.name || '').trim();
      if (!name) return null;
      return { name, isKey: s.isKeySystem === true };
    })
    .filter(Boolean);

  const documentItems = (documents || [])
    .map((d) => {
      const name = (d?.name || '').trim();
      if (!name) return null;
      return { name, isReport: d.docType === 'report' };
    })
    .filter(Boolean);

  const descriptionText = (description || '').trim();
  const actorText = (actor || '').trim();

  const modal =
    open &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{ zIndex: 10000 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          className="bg-slate-50 rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden mx-4 ring-1 ring-slate-200"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Node details"
        >
          <div className="flex items-center justify-between p-6 pb-4 border-b border-slate-200 bg-slate-100/80 shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Eye className="h-5 w-5 text-slate-600" />
              Node details
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close node details"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4 bg-slate-50">
            <DetailField label="Node Type" labelClassName="text-blue-600">
              <NodeTypeBadge nodeType={nodeType} />
            </DetailField>

            <DetailField label="Description" labelClassName="text-slate-600">
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words">
                {descriptionText || '—'}
              </div>
            </DetailField>

            <DetailField label="Actor (Swimlane)" labelClassName="text-sky-700">
              {actorText ? (
                <span
                  className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium"
                  style={{ backgroundColor: '#e0f2fe', color: '#0369a1', boxShadow: 'inset 0 0 0 1px #7dd3fc' }}
                >
                  {actorText}
                </span>
              ) : (
                <span className="text-sm text-slate-400">—</span>
              )}
            </DetailField>

            <DetailField label="System" labelClassName="text-violet-700">
              {systemItems.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {systemItems.map((s) => (
                    <SystemChip key={s.name} name={s.name} isKey={s.isKey} />
                  ))}
                </div>
              ) : (
                <span className="text-sm text-slate-400">—</span>
              )}
            </DetailField>

            <DetailField label="Document" labelClassName="text-emerald-700">
              {documentItems.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {documentItems.map((d) => (
                    <DocumentChip key={d.name} name={d.name} isReport={d.isReport} />
                  ))}
                </div>
              ) : (
                <span className="text-sm text-slate-400">No documents linked</span>
              )}
            </DetailField>

            <p className="text-xs text-gray-400 border-t border-slate-200 pt-3">
              Double-click the node to edit
            </p>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1 right-1 z-50 rounded bg-white/95 p-1 text-slate-500 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-800"
        title="View node details"
        aria-label="View node details"
      >
        <Eye className="h-3.5 w-3.5" />
      </button>
      {modal}
    </>
  );
}

export default FlowchartNodeHoverCard;
